import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { CONFIG } from "../config";
import moment from "moment-timezone";
import i18next from "i18next";
import { useUserOptions, useUpdateUserOptions } from "./queries/useUserOptions";
import { getUserTimezone } from "../utils/timezone";
import { UtilityService } from "../services/UtilityService";

const VALID_TIME_FORMATS = ["HH:mm", "HH:mm:ss", "h:mm:ssa", "h:mma"];
const VALID_SPEED_UNITS = ["km/h", "mph"];

// Defaults mirroring useUserPreferences initial state
function getLocalStorageOptions() {
  const timeFormat = localStorage.getItem("time_format");
  const speedUnit = localStorage.getItem("speed_unit");

  return {
    language: localStorage.getItem("language") ?? "en",
    timezone: localStorage.getItem("timezone") ?? moment.tz.guess(),
    time_format: VALID_TIME_FORMATS.includes(timeFormat)
      ? timeFormat
      : "HH:mm:ss",
    temperature_unit: localStorage.getItem("temperature_unit") ?? "C",
    speed_unit: VALID_SPEED_UNITS.includes(speedUnit) ? speedUnit : "km/h",
    date_format:
      localStorage.getItem("date_format") ?? CONFIG.defaultDateFormat,
    decimal_separator:
      localStorage.getItem("decimal_separator") ??
      CONFIG.defaultDecimalSeparator ??
      ".",
  };
}

/**
 * Runs once per session after the user is initialized.
 *
 * - If the server has no options yet (all null) → pushes current
 *   localStorage values so the server is populated.
 * - If the server has values → syncs them back to localStorage so
 *   any part of the app reading localStorage directly stays accurate.
 */
export function useUserOptionsSync() {
  const user = useSelector((state) => state.user);
  const isUserReady = !!user?.id;
  const hasSynced = useRef(false);

  const { data: serverOptions, isSuccess } = useUserOptions(isUserReady);
  const { mutate: updateServerOptions } = useUpdateUserOptions();

  useEffect(() => {
    if (!isSuccess || hasSynced.current) return;

    hasSynced.current = true;

    const allNull = Object.values(serverOptions).every((v) => v === null);

    if (allNull) {
      // First time: seed the server with what the user already has locally
      updateServerOptions(getLocalStorageOptions());
      return;
    }

    // Sync non-null server values back to localStorage
    const fieldMap = {
      language: "language",
      timezone: "timezone",
      time_format: "time_format",
      temperature_unit: "temperature_unit",
      speed_unit: "speed_unit",
      date_format: "date_format",
      decimal_separator: "decimal_separator",
    };

    let languageChanged = false;
    let timezoneChanged = false;
    const previousTimezone = getUserTimezone();

    for (const [apiKey, localKey] of Object.entries(fieldMap)) {
      if (serverOptions[apiKey] !== null) {
        localStorage.setItem(localKey, serverOptions[apiKey]);
        if (
          apiKey === "language" &&
          serverOptions[apiKey] !== i18next.language
        ) {
          languageChanged = true;
        }
        if (
          apiKey === "timezone" &&
          serverOptions[apiKey] !== previousTimezone
        ) {
          timezoneChanged = true;
        }
      }
    }

    if (languageChanged) {
      i18next.changeLanguage(serverOptions.language);
    }

    if (timezoneChanged) {
      // The dateRange reducer's default ("today") was computed at store-init
      // time using the browser-guessed timezone, before the real configured
      // timezone was known. Recompute it now that localStorage.timezone is
      // correct, or the default range stays anchored to the wrong day boundaries.
      UtilityService.updateDateRange();
    }
  }, [isSuccess, serverOptions, updateServerOptions]);
}
